import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default function FlowMap({
  zones,
  geojson,
  origins,
  destinations,
  flows,
  onToggleZone,
  lassoActive,
  onLassoSelect,
  showPolygons = true,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({ polygons: null, markers: null, flows: null, lasso: null });
  const lassoStateRef = useRef({ active: false, start: null, rect: null });

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [40.4, -3.7],
      zoom: 6,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
    });

    // Defensive monkey-patch: after `map.remove()` Leaflet deletes
    // `_mapPane` but leaves `_loaded=true`, so any deferred call to
    // `invalidateSize` (e.g. an in-flight requestAnimationFrame from a
    // window resize triggered while the panel is being dragged) blows up
    // inside `_rawPanBy` with `Cannot read properties of undefined
    // (reading '_leaflet_pos')`. Wrap the call so it bails out safely.
    const origInvalidate = map.invalidateSize.bind(map);
    map.invalidateSize = function (opts) {
      if (!this._mapPane || !this._container || !this._container.isConnected) {
        return this;
      }
      try {
        return origInvalidate(opts);
      } catch (_) {
        return this;
      }
    };
    // CARTO Basemaps: append the API key when it's configured so we hit the
    // authenticated endpoint (higher quota + guaranteed uptime). The tile
    // pattern `{x}/{y}{r}.png` still works — CARTO reads the token from
    // the query string as `key` (NOT `api_key`).
    const cartoKey = process.env.REACT_APP_CARTO_API_KEY;
    const cartoQS = cartoKey ? `?key=${cartoKey}` : "";
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png${cartoQS}`, {
      attribution: '© OpenStreetMap · © CARTO · datos MITMA',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png${cartoQS}`, {
      subdomains: "abcd",
      maxZoom: 19,
      pane: "shadowPane",
    }).addTo(map);
    layersRef.current.polygons = L.layerGroup().addTo(map);
    layersRef.current.markers = L.layerGroup().addTo(map);
    layersRef.current.flows = L.layerGroup().addTo(map);
    layersRef.current.lasso = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Watch container size so the map redraws correctly when its panel is
    // resized (react-grid-layout drag/resize, sidebar toggles, etc.).
    // We debounce + guard against the map being torn down or not yet fully
    // initialised (no _mapPane) — otherwise Leaflet throws a `_leaflet_pos`
    // TypeError when invalidateSize fires during a resize race.
    let resizeObs;
    let resizeTimer;
    let initialTimer;
    const safeInvalidate = () => {
      const m = mapRef.current;
      if (!m || !m._mapPane || !m._container || !m._container.isConnected) return;
      try { m.invalidateSize({ animate: false }); } catch (_) {}
    };
    initialTimer = setTimeout(safeInvalidate, 100);
    if (typeof ResizeObserver !== "undefined") {
      resizeObs = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(safeInvalidate, 80);
      });
      resizeObs.observe(containerRef.current);
    }
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(resizeTimer);
      try { resizeObs && resizeObs.disconnect(); } catch (_) {}
      try { map.remove(); } catch (_) {}
      mapRef.current = null;
    };
  }, []);

  // GeoJSON polygons
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !layersRef.current.polygons) return;
    layersRef.current.polygons.clearLayers();
    if (!showPolygons || !geojson) return;

    const layer = L.geoJSON(geojson, {
      style: (feature) => {
        const id = feature.properties.id;
        const isO = origins.includes(id);
        const isD = destinations.includes(id);
        if (isO && isD) {
          return { color: "#FFD740", weight: 2, fillOpacity: 0.18, fillColor: "#FFD740" };
        }
        if (isO) return { color: "#00E5FF", weight: 2, fillOpacity: 0.18, fillColor: "#00E5FF" };
        if (isD) return { color: "#FF5722", weight: 2, fillOpacity: 0.18, fillColor: "#FF5722" };
        return { color: "#3F3F46", weight: 0.8, fillOpacity: 0.06, fillColor: "#FF9800" };
      },
      onEachFeature: (feature, layer) => {
        const p = feature.properties;
        layer.bindTooltip(
          `<div style="font-family:'JetBrains Mono'; font-size:11px; padding:2px;">
            <div style="color:#FF9800; text-transform:uppercase; letter-spacing:0.15em; font-size:9px;">${p.level}</div>
            <div style="color:white">${p.name}</div>
            <div style="color:#888">${p.id}</div>
          </div>`,
          { sticky: true }
        );
        layer.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          if (onToggleZone) onToggleZone({ id: p.id, name: p.name });
        });
      },
    });
    layer.addTo(layersRef.current.polygons);
  }, [geojson, origins, destinations, showPolygons, onToggleZone]);

  // Markers (only for selected zones, polygons handle the rest)
  useEffect(() => {
    if (!mapRef.current || !layersRef.current.markers) return;
    layersRef.current.markers.clearLayers();
    const selectedIds = new Set([...origins, ...destinations]);
    if (selectedIds.size === 0) return;
    zones.forEach((z) => {
      if (!selectedIds.has(z.id)) return;
      const isO = origins.includes(z.id);
      const isD = destinations.includes(z.id);
      let cls = "zone-dot";
      if (isO) cls = "zone-dot selected-origin";
      else if (isD) cls = "zone-dot selected-destination";
      const icon = L.divIcon({
        className: "zone-marker",
        html: `<div class="${cls}" title="${z.name}"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const marker = L.marker([z.lat, z.lng], { icon, interactive: !!onToggleZone });
      if (onToggleZone) marker.on("click", () => onToggleZone(z));
      layersRef.current.markers.addLayer(marker);
    });
  }, [zones, origins, destinations, onToggleZone]);

  // Flows
  useEffect(() => {
    if (!mapRef.current || !layersRef.current.flows) return;
    layersRef.current.flows.clearLayers();
    if (!flows || flows.length === 0) return;
    const max = Math.max(...flows.map((f) => f.trips), 1);
    flows.forEach((f) => {
      if (f.origin_id === f.destination_id) return;
      const o = zones.find((z) => z.id === f.origin_id);
      const d = zones.find((z) => z.id === f.destination_id);
      if (!o || !d || f.trips <= 0) return;
      const w = 1 + (f.trips / max) * 6;
      const opacity = 0.25 + (f.trips / max) * 0.6;
      const line = L.polyline(
        [[o.lat, o.lng], [d.lat, d.lng]],
        { color: "#FF5722", weight: w, opacity, lineCap: "round" }
      );
      line.bindTooltip(
        `<div style="font-family:'JetBrains Mono'; font-size:11px;">
          <div style="color:#00E5FF">${o.name}</div>
          <div style="color:#888">↓</div>
          <div style="color:#FF5722">${d.name}</div>
          <div style="color:white; margin-top:4px;">${Number(f.trips).toLocaleString("es-ES")} viajes · ${f.distance_km} km</div>
        </div>`
      );
      layersRef.current.flows.addLayer(line);
    });
  }, [flows, zones]);

  // Lasso (rectangle) selection
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const container = map.getContainer();
    const onDown = (e) => {
      if (!lassoActive) return;
      if (e.button !== 0) return;
      L.DomEvent.stopPropagation(e);
      L.DomEvent.preventDefault(e);
      map.dragging.disable();
      const start = map.mouseEventToLatLng(e);
      lassoStateRef.current.start = start;
      lassoStateRef.current.active = true;
      if (lassoStateRef.current.rect) {
        layersRef.current.lasso.removeLayer(lassoStateRef.current.rect);
      }
      lassoStateRef.current.rect = L.rectangle([start, start], {
        color: "#FF9800",
        weight: 1.5,
        fillColor: "#FF9800",
        fillOpacity: 0.08,
        dashArray: "4 4",
      }).addTo(layersRef.current.lasso);
    };
    const onMove = (e) => {
      if (!lassoStateRef.current.active || !lassoStateRef.current.rect) return;
      const cur = map.mouseEventToLatLng(e);
      lassoStateRef.current.rect.setBounds([lassoStateRef.current.start, cur]);
    };
    const onUp = (e) => {
      if (!lassoStateRef.current.active) return;
      map.dragging.enable();
      lassoStateRef.current.active = false;
      const rect = lassoStateRef.current.rect;
      if (!rect) return;
      const bounds = rect.getBounds();
      const inside = zones.filter((z) => bounds.contains([z.lat, z.lng]));
      if (onLassoSelect) onLassoSelect(inside);
      // remove rectangle after selection
      setTimeout(() => {
        if (lassoStateRef.current.rect) {
          layersRef.current.lasso.removeLayer(lassoStateRef.current.rect);
          lassoStateRef.current.rect = null;
        }
      }, 250);
    };

    container.addEventListener("mousedown", onDown);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseup", onUp);
    return () => {
      container.removeEventListener("mousedown", onDown);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseup", onUp);
    };
  }, [lassoActive, zones, onLassoSelect]);

  // Toggle cursor when lasso active
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getContainer().style.cursor = lassoActive ? "crosshair" : "";
  }, [lassoActive]);

  return <div ref={containerRef} className="w-full h-full" data-testid="flow-map" />;
}
